<template>
  <div>
    <AppDialog :is-open="isDialogOpened"
               size="xl"
               data-locator="bibles-generation-dialog"
               @cancel="closeDialog"
    >
      <template #title>
        {{ $t('project.bibles.dialogs.BibleGenerateDialog.generateNewBible') }}
      </template>

      <template #body>
        <template v-if="formStep === 1">
          <AppTextField v-model.trim="v$.newBibleName.$model"
                        :label="$t('project.bibles.dialogs.BibleGenerateDialog.newBibleNameLabel')"
                        class="required"
                        counter="100"
                        :error-messages="hasNewBibleNameErrors"
                        maxLength="100"
                        data-locator="bible-name"
          />

          <div class="mt-8">
            <p class="mb-4">{{ $t("project.bibles.dialogs.BibleGenerateDialog.documentsSelectionTitle") }}</p>

            <v-tabs v-model="currentTab" fixed-tabs color="deep-purple-accent-4">
              <v-tab value="documents" data-locator="documents-tab">{{ $t("project.bibles.dialogs.BibleGenerateDialog.header1") }}</v-tab>
              <v-tab v-if="!noTodoFolder" value="todos" data-locator="checklist-attachments-tab">{{ $t("project.bibles.dialogs.BibleGenerateDialog.header2") }}</v-tab>
              <v-tab v-if="!currentRoom.isDataroom" value="signatures" data-locator="signed-documents-tab">{{ $t('project.bibles.dialogs.BibleGenerateDialog.header3') }}</v-tab>
            </v-tabs>
            <v-window v-model="currentTab" class="py-4">
              <v-window-item value="documents">
                <v-card-text style="max-height: 300px; overflow-y: auto">
                  <template v-if="documentsFolder">
                    <template v-if="documentsFolder.children.length !== 0">
                      <AppClosdFildersTree v-model:value="selectedDocuments" :root-folder="documentsFolder" />
                    </template>
                    <template v-else>
                      <div class="text-caption text-center">
                        {{$t('project.bibles.dialogs.BibleGenerateDialog.noDocuments')}}
                      </div>
                    </template>
                  </template>
                  <template v-else>
                    <div class="text-center">
                      <v-progress-circular
                        style="width: 48px; margin: 100px 0"
                        color="primary"
                        size="48"
                        indeterminate
                      />
                    </div>
                  </template>
                </v-card-text>
              </v-window-item>
              <v-window-item v-if="!noTodoFolder" value="todos">
                <v-card-text style="max-height: 300px; overflow-y: auto">
                  <template v-if="!todosFolderStructureError">
                    <template v-if="sortedTodosFolder">
                      <template v-if="sortedTodosFolder.children.length !== 0">
                        <AppClosdFildersTree v-model:value="selectedTodos"
                                            :root-folder="todosFolderStructure"
                                            :display-tree-structure="displayTreeStructure"
                        />
                      </template>
                      <template v-else>
                        <div class="text-caption text-center">
                          {{$t('project.bibles.dialogs.BibleGenerateDialog.noTasks')}}
                        </div>
                      </template>
                    </template>
                    <template v-else>
                      <div class="text-center">
                        <v-progress-circular
                          style="width: 48px; margin: 100px 0"
                          color="primary"
                          size="48"
                          indeterminate
                        />
                      </div>
                    </template>
                  </template>
                  <div v-else class="text-caption text-center">
                    {{ $t('project.bibles.dialogs.BibleGenerateDialog.checklistTodosFolderError') }}
                  </div>
                </v-card-text>
              </v-window-item>
              <v-window-item v-if="!currentRoom.isDataroom" value="signatures">
                <v-card-text style="max-height: 300px; overflow-y: auto">
                  <template v-if="mySignaturesFolder">
                    <template v-if="mySignaturesFolder.children.length !== 0">
                      <SignedDocumentsSelector :signed-documents="mySignaturesFolder.children"
                                               v-model:selected-signed-documents="selectedSignatureIntern"
                      />
                    </template>
                    <template v-else>
                      <div class="text-caption text-center">
                        {{ $t('project.bibles.dialogs.BibleGenerateDialog.noSignedDocuments') }}
                      </div>
                    </template>
                  </template>
                  <template v-else>
                    <div class="text-center">
                      <v-progress-circular
                        style="width: 48px; margin: 100px 0"
                        color="primary"
                        size="48"
                        indeterminate
                      />
                    </div>
                  </template>
                </v-card-text>
              </v-window-item>
            </v-window>
          </div>
        </template>

        <template v-else-if="formStep === 2">
          <p>{{ $t("project.bibles.dialogs.BibleGenerateDialog.optionsTitle") }}</p>

          <div class="d-flex flex-column row-gap-2 mt-6">
            <template v-if="areCertificateOptionsAvailable">
              <template v-if="roomEnvelopesDownloadPreflight.canIncludeCertificateLocale">
                <v-checkbox v-model="certificateOptions.selectLocaleAuthenticationCertificate"
                            :label="$t('project.bibles.dialogs.BibleGenerateDialog.selectAuthenticationCertificateWithLanguage', { language: displayedCertificateLocale })"
                            :disabled="certificateOptions.selectAuthenticationCertificate || certificateOptions.selectMergeDocuments"
                            name="withLocaleCertificate"
                            density="compact"
                            hide-details
                            data-locator="include-locale-certificates-option"
                />

                <v-checkbox v-model="certificateOptions.selectAuthenticationCertificate"
                            :label="$t('project.bibles.dialogs.BibleGenerateDialog.selectAuthenticationCertificateWithLanguage', { language: DEFAULT_CERTIFICATE_LANGUAGE })"
                            :disabled="certificateOptions.selectLocaleAuthenticationCertificate || certificateOptions.selectMergeDocuments"
                            name="withDefaultCertificate"
                            density="compact"
                            hide-details
                            data-locator="include-default-certificates-option"
                />
              </template>
              <template v-else>
                <v-checkbox v-model="certificateOptions.selectAuthenticationCertificate"
                            :label="$t('project.bibles.dialogs.BibleGenerateDialog.selectAuthenticationCertificate')"
                            :disabled="certificateOptions.selectMergeDocuments"
                            name="withDefaultCertificate"
                            density="compact"
                            hide-details
                            data-locator="include-certificates-option"
                />
              </template>

              <template v-if="isDocumentsMergingAvailable">
                <AppTooltip :disabled="canEveryDocumentsBeMerged" :max-width="410" data-locator="bibles-merge-certificates-tooltip">
                  <template #activator="{ props }">
                    <div class="d-flex align-center mt-1" v-bind="props">
                      <v-checkbox v-model="certificateOptions.selectMergeDocuments"
                                  :label="$t('project.bibles.dialogs.BibleGenerateDialog.selectAuthenticationCertificate')"
                                  :disabled="!isAnyCertificateSelected || !canEveryDocumentsBeMerged"
                                  name="mergeDocuments"
                                  class="mt-0 pt-0 d-flex align-center"
                                  density="compact"
                                  hide-details
                                  data-locator="merge-certificates-option"
                      >
                        <template #label>
                          <span>
                            {{ $t("project.bibles.dialogs.BibleGenerateDialog.mergeDocuments") }}
                          </span>
                        </template>
                      </v-checkbox>
                    </div>
                  </template>
                  <span>{{ $t("project.bibles.dialogs.BibleGenerateDialog.documentsMergeNotAvailableForEveryDocumentsTooltip") }}</span>
                </AppTooltip>

                <div class="text-caption ml-7" data-locator="merge-certificate-info">
                  {{ $t("project.bibles.dialogs.BibleGenerateDialog.mergeDocumentsHint") }}
                </div>
              </template>
            </template>

            <v-checkbox v-model="includeFilesIndex"
                        :label="$t('project.bibles.dialogs.BibleGenerateDialog.includeFilesIndexLabel')"
                        density="compact"
                        hide-details
                        data-locator="include-files-index-option"
            />

            <v-checkbox v-if="displayHistoryOption"
                        v-model="includeHistory"
                        :label="$t('project.bibles.dialogs.BibleGenerateDialog.includeHistoryLabel')"
                        density="compact"
                        hide-details
                        data-locator="include-files-versions-history-option"
            />

            <v-checkbox v-if="!currentRoom.isDataroom"
                        v-model="hasChecklist"
                        :label="$t('project.bibles.dialogs.BibleGenerateDialog.hasChecklist')"
                        density="compact"
                        hide-details
                        data-locator="include-checklist-export-option"
            />

            <v-checkbox v-if="currentRoom.isDataroom"
                        v-model="hasQA"
                        :label="$t('project.bibles.dialogs.BibleGenerateDialog.hasQA')"
                        density="compact"
                        hide-details
                        data-locator="include-q-and-a-export-option"
            />
            <div v-if="displayNdaOption" class="d-flex align-center">
              <v-checkbox v-model="hasNdaToDownload"
                          :label="$t('project.bibles.dialogs.BibleGenerateDialog.hasNda')"
                          density="compact"
                          hide-details
                          data-locator="include-signed-ndas-option"
              />
              <AppFeatureTag tag="new" class="ml-2"/>
            </div>
          </div>
        </template>
      </template>

      <template #footer>
        <div class="d-flex w-100 column-gap-2 align-center justify-end">
          <app-button
            v-if="formStep === 2"
            class="mr-auto"
            type="outlined"
            variant="neutral"
            is-icon-color-custom
            data-locator="previous-button"
            @click="formStep = 1"
          >
            <template #left-icon>
              <app-material-icon name="arrow_back" size="xs" />
            </template>
            {{$t('common.previous')}}
          </app-button>

          <app-button
            type="outlined"
            variant="neutral"
            :disabled="postBiblePending"
            data-locator="cancel-button"
            @click="closeDialog"
          >
            {{$t('common.cancel')}}
          </app-button>

          <app-button
            v-if="formStep === 1"
            type="primary"
            :loading="roomEnvelopesDownloadPreflightPending"
            :disabled="!canValidateSelectionStep"
            data-locator="next-button"
            @click="nextStep"
          >
            {{ $t("common.next") }}
          </app-button>
          <app-button
            v-else
            type="primary"
            :loading="postBiblePending"
            :disabled="!canPostBible"
            data-locator="confirm-button"
            @click="preparePostBible"
          >
            {{ $t("project.bibles.dialogs.BibleGenerateDialog.validateBibleCreation") }}
          </app-button>
        </div>
      </template>
    </AppDialog>

    <BibleTooBigDialog v-model:isOpen="bibleTooBigModalIsOpen"
                       :bibleTooBigData="bibleTooBigData"
    />
  </div>
</template>

<script>

import { useVuelidate } from '@vuelidate/core'
import { maxLength, required } from '@vuelidate/validators'
import { orderBy } from 'lodash-es'
import { mapActions, mapGetters, mapState } from 'vuex'

import AppClosdFildersTree from '@/common/app-closd-filders-tree/AppClosdFildersTree.vue'
import AppFeatureTag from '@/common/AppFeatureTag.vue'
import AppTextField from '@/common/AppTextField.vue'
import AppTooltip from '@/common/AppTooltip.vue'
import AppDialog from '@/common/dialogs/AppDialog.vue'
import { track, USER_ROLE } from '@/common/pendo/agent'
import { formFieldErrors } from '@/common/utils/form'
import { PRODUCT_ROOM_TYPES } from '@/common/utils/roomTypes'
import { toast } from '@/common/utils/toast'
import { dialogMixin } from '@/mixins/dialogMixin'
import SignedDocumentsSelector from '@/project/bibles/dialogs/bible-generation/SignedDocumentsSelector.vue'
import BibleTooBigDialog from '@/project/bibles/dialogs/BibleTooBigDialog.vue'
import { BiblesActionTypes } from '@/store/modules/bibles/action_types'
import { GET_DOCUMENTS_BY_ID } from '@/store/modules/documents/action_types'
import { EnvelopesActionTypes } from '@/store/modules/envelopes/action_types'
import { GET_ROOM_ENVELOPES_DOWNLOAD_PREFLIGHT } from '@/store/modules/room/action_types'
import { GET_TODOS_FOLDER_STRUCTURE } from '@/store/modules/todo/action_types'

const DEFAULT_CERTIFICATE_LANGUAGE = 'EN'

export const BIBLE_GENERATE_DIALOG_TABS = {
  DOCUMENTS: 'documents',
  TODOS: 'todos',
  SIGNATURES: 'signatures',
}

export default {
  name: 'BibleGenerateDialog',
  emits: ['selectedSignature', 'pollBibles'],
  components: {
    SignedDocumentsSelector,
    AppClosdFildersTree,
    AppFeatureTag,
    AppDialog,
    AppTextField,
    AppTooltip,
    BibleTooBigDialog,
  },
  setup: () => ({ v$: useVuelidate() }),
  mixins: [dialogMixin],
  validations: {
    newBibleName: {
      required,
      maxLength: maxLength(100),
    },
  },
  props: {
    initialSelectedDocuments: {
      type: Array,
      required: false,
      default: () => [],
    },
    initialSelectedTodos: {
      type: Array,
      required: false,
      default: () => [],
    },
    initialTab: {
      type: String,
      default: 'documents',
    },
    /**
     * Displays tree titles and subtitles
     */
     displayTreeStructure: {
      type: Boolean,
      default: false,
    },
    mnemo: {
      type: String,
      required: true,
    },
    pollAfterPost: {
      type: Boolean,
      default: true,
    },
    selectedSignatures: {
      type: Array,
      required: false,
      default: () => [],
    },
  },
  data () {
    return {
      DEFAULT_CERTIFICATE_LANGUAGE,
      bibleTooBigData: {
        currentSize: null,
        maxSize: null,
      },
      bibleTooBigModalIsOpen: false,
      certificateOptions: {
        selectAuthenticationCertificate: false,
        selectLocaleAuthenticationCertificate: false,
        selectMergeDocuments: false,
      },
      currentTab: null,
      dialogIsOpen: false,
      documentsFolder: null,
      formStep: 1,
      hasChecklist: false,
      hasNdaToDownload: false,
      hasQA: false,
      includeHistory: false,
      includeFilesIndex: true,
      mySignaturesFolder: null,
      newBibleName: '',
      noSignatureSelected: false,
      noTodoFolder: false,
      selectedDocuments: [],
      selectedSignatureIntern: [],
      selectedTodos: [],
      sortedTodosFolder: null,
    }
  },
  computed: {
    ...mapGetters('room', ['isCurrentUserPm']),
    ...mapState('bibles', ['postBiblePending']),
    ...mapState('documents', ['documents']),
    ...mapState('envelopes', ['myEnvelopes']),
    ...mapState('todo', ['todosFolderStructure', 'todosFolderStructureError']),
    ...mapState('room', ['currentRoom', 'roomEnvelopesDownloadPreflight', 'roomEnvelopesDownloadPreflightPending']),
    allSelectedFiles () {
      return [...this.selectedDocuments, ...this.selectedTodos.flatMap(todo => todo?.children || todo), ...this.selectedSignatureIntern]
    },
    areCertificateOptionsAvailable () {
      return this.selectedSignatureIntern.length > 0 && (this.roomEnvelopesDownloadPreflight?.canIncludeCertificate || this.roomEnvelopesDownloadPreflight?.canIncludeCertificateLocale)
    },
    canEveryDocumentsBeMerged () {
      if (this.certificateOptions.selectAuthenticationCertificate) {
        return this.roomEnvelopesDownloadPreflight.canIncludeCombinedCertificate
      } else if (this.certificateOptions.selectLocaleAuthenticationCertificate) {
        return this.roomEnvelopesDownloadPreflight.canIncludeCombinedCertificateLocale
      }

      return true
    },
    canValidateSelectionStep () {
      return this.newBibleName.length > 0 && this.isAnyFileSelected
    },
    canPostBible () {
      return !this.postBiblePending && this.isAnyFileSelected
    },
    displayedCertificateLocale () {
      return this.roomEnvelopesDownloadPreflight?.certificateLocale.toUpperCase() || null
    },
    displayHistoryOption () {
      return this.allSelectedFiles.some(document => document.type !== 'file' || document?.nbVersions > 1 || document?.version > 1)
    },
    displayNdaOption () {
      return this.currentRoom?.isDataroom && this.isCurrentUserPm && this.currentRoom?.hasNda
    },
    hasNewBibleNameErrors () {
      return formFieldErrors(
        this.v$.newBibleName,
        [
          ['required', this.$t('common.validations.fieldRequired', { fieldName: this.$t('project.bibles.dialogs.BibleGenerateDialog.newBibleNameLabel') })],
          ['maxLength', this.$t('common.validations.maxLength', { fieldName: this.$t('project.bibles.dialogs.BibleGenerateDialog.newBibleNameLabel') })],
        ],
      )
    },
    isAnyFileSelected () {
      return this.selectedDocuments.length > 0 || this.selectedTodos.length > 0 || this.selectedSignatureIntern.length > 0
    },
    isAnyCertificateSelected () {
      return this.certificateOptions.selectAuthenticationCertificate || this.certificateOptions.selectLocaleAuthenticationCertificate
    },
    isDocumentsMergingAvailable () {
      return this.isCurrentUserPm
    },
  },
  watch: {
    selectedSignatureIntern (signedDocuments) {
      if (signedDocuments.length === 0) {
        this.certificateOptions = {
          selectAuthenticationCertificate: false,
          selectLocaleAuthenticationCertificate: false,
          selectMergeDocuments: false,
        }
      }
    },
  },
  created () {
    this.noTodoFolder = this.currentRoom.isDataroom
  },
  mounted () {
    this.getRoomFiles()
    this.currentTab = this.initialTab
    this.selectedSignatureIntern = this.selectedSignatures
    this.noSignatureSelected = this.selectedSignatureIntern.length === 0
    this.selectedDocuments = this.initialSelectedDocuments
    this.selectedTodos = this.initialSelectedTodos
  },
  methods: {
    ...mapActions('bibles', [BiblesActionTypes.POST_BIBLE]),
    ...mapActions('documents', [GET_DOCUMENTS_BY_ID]),
    ...mapActions('envelopes', [EnvelopesActionTypes.GET_MY_ENVELOPES]),
    ...mapActions('room', [GET_ROOM_ENVELOPES_DOWNLOAD_PREFLIGHT]),
    ...mapActions('todo', [GET_TODOS_FOLDER_STRUCTURE]),
    onCloseDialog () {
      this.$emit('selectedSignature', this.selectedSignatureIntern)
      this.closeDialog()
    },
    async nextStep () {
      if (!this.isDocumentsMergingAvailable) {
        this.certificateOptions.selectAuthenticationCertificate = false
        this.certificateOptions.selectLocaleAuthenticationCertificate = false
        this.certificateOptions.selectMergeDocuments = false
      }

      if (this.selectedSignatureIntern.length !== 0) {
        await this.getRoomEnvelopesDownloadPreflight()
      }

      this.formStep = 2
    },
    async getRoomEnvelopesDownloadPreflight () {
      const envelopeIds = this.selectedSignatureIntern.map(envelope => { return envelope.id }).join(',')

      try {
        await this.GET_ROOM_ENVELOPES_DOWNLOAD_PREFLIGHT({
          mnemo: this.mnemo,
          params: {
            envelopesIds: envelopeIds,
          },
        })
      } catch (error) {
        console.error(error)
      }
    },
    getRoomFiles () {
      this.documentsFolder = null
      this.sortedTodosFolder = null
      this.mySignaturesFolder = null
      this.getDocumentsFolder()
      if (!this.noTodoFolder) this.getTodosFolder()
      if (!this.currentRoom.isDataroom) this.getMySignaturesFolder()
    },
    async getDocumentsFolder () {
      await this.GET_DOCUMENTS_BY_ID({
        mnemo: this.mnemo,
        queryObject: { ignoreTodoFolder: true, lazyMode: true },
        id: 0,
      })
      this.documentsFolder = {
        ...this.documents,
        children: orderBy(this.documents.children, ['type', 'basename'], ['desc', 'asc']),
      }
    },
    async getTodosFolder () {
      try {
        await this.GET_TODOS_FOLDER_STRUCTURE({
          mnemo: this.mnemo,
          queryObject: { lazyMode: false },
        })
        this.sortedTodosFolder = {
          ...this.todosFolderStructure,
          children: orderBy(this.todosFolderStructure.children, ['type', 'basename'], ['desc', 'asc']),
        }
      } catch (error) {
        if (error.response?.status === 403) {
          this.noTodoFolder = true
        }
        toast.error(this.$t('project.bibles.dialogs.BibleGenerateDialog.checklistTodosFolderError'))
      }
    },
    async getMySignaturesFolder () {
      await this.GET_MY_ENVELOPES({
        mnemo: this.mnemo,
        queryObject: {
          lazyMode: false,
          downloadableOnly: true,
        },
      })
      this.mySignaturesFolder = {
        basename: this.$t('project.bibles.dialogs.BibleGenerateDialog.header3'),
        name: this.$t('project.bibles.dialogs.BibleGenerateDialog.header3'),
        children: orderBy(this.myEnvelopes, ['type', 'basename'], ['desc', 'asc']),
      }
    },
    matchResponseError(response, { status, errorCode }) {
      return response?.status === status &&
        response.data?.errorCode === errorCode
    },
    handleBibleTooLargeError(data) {
      this.bibleTooBigData.currentSize = data.currentSize
      this.bibleTooBigData.maxSize = data.maxSize
      this.bibleTooBigModalIsOpen = true
    },
    handleInvalidRightsError() {
      toast.error(this.$t('project.bibles.dialogs.BibleGenerateDialog.invalidRightsBibleError'))
    },
    handleGenericError() {
      toast.error(this.$t('project.bibles.dialogs.BibleGenerateDialog.postBibleError'))
      this.closeDialog()
    },
    handlePostSuccess() {
      if (this.pollAfterPost) {
        this.$emit('poll-bibles')
      }
      toast.success(this.$t('project.bibles.dialogs.BibleGenerateDialog.postBibleSuccess'))
      track('project_bible', {
        project_mnemo: this.mnemo,
        user_role: this.isCurrentUserPm ? USER_ROLE.PROJECT_MANAGER : USER_ROLE.GUEST,
        project_type: this.currentRoom.isDataroom ? PRODUCT_ROOM_TYPES.DATAROOM : PRODUCT_ROOM_TYPES.LEGAL_PROJECT,
      })
      this.onCloseDialog()
    },
    async preparePostBible () {
      this.v$.$touch()

      if (!this.v$.$invalid) {
        const envelopeIds = []
        const fileIds = []
        const folderIds = []
        const todoFileIds = []
        const todoFolderIds = []

        if (this.selectedDocuments) {
          this.selectedDocuments.forEach(item => {
            if (item.type === 'file') {
              fileIds.push(item.id)
            } else if (item.type === 'folder') {
              folderIds.push(item.id)
            }
          })
        }

        if (this.selectedTodos) {
          this.selectedTodos.forEach(item => {
            if (item.type === 'file') {
              todoFileIds.push(item.id)
            } else if (item.type === 'folder') {
              todoFolderIds.push(item.id)
            }
          })
        }

        if (this.selectedSignatureIntern) {
          this.selectedSignatureIntern.forEach(item => {
            envelopeIds.push(item.id)
          })
        }

        try {
          await this.POST_BIBLE({
            mnemo: this.mnemo,
            data: {
              certificate: this.certificateOptions.selectAuthenticationCertificate,
              certificateLocale: this.certificateOptions.selectLocaleAuthenticationCertificate,
              combined: this.certificateOptions.selectMergeDocuments,
              envelopeIds: envelopeIds,
              fileIds: fileIds,
              folderIds: folderIds,
              hasChecklist: this.hasChecklist,
              hasCustom: true,
              hasIndex: this.includeFilesIndex,
              hasNda: this.hasNdaToDownload,
              hasQA: this.hasQA,
              history: this.includeHistory,
              name: this.newBibleName,
              todoFileIds: todoFileIds,
              todoFolderIds: todoFolderIds,
            },
          })

          this.handlePostSuccess()
        } catch (error) {
          const errorResponse = error.response

          if (this.matchResponseError(errorResponse, {
            status: 422,
            errorCode: 'ERROR_BIBLE_TOO_LARGE',
          })) {
            this.handleBibleTooLargeError(errorResponse.data)
            return
          }

          if (this.matchResponseError(errorResponse, {
            status: 403,
            errorCode: 'ERR_INVALID_RIGHTS',
          })) {
            this.handleInvalidRightsError()
            return
          }

          this.handleGenericError()
        }
      }
    },
  },
}
</script>

<style scoped lang="scss">
:deep(*) {
  .v-btn {
    color: rgb(var(--v-theme-primary));

    &__content {
      font-size: 16px;
      color: rgb(var(--v-theme-accent-lighten-4));
      letter-spacing: 1.4px;
    }
  }

  .v-tab--selected {
    .v-btn__content {
      color: rgb(var(--v-theme-primary));
    }
  }
}
</style>
